// Each living Happy Hour sign shaves 1 mana off every card while it stands. Counted
// from the minions on every call rather than cached on the player, so a synced state
// cannot smuggle in a discount its minions do not back up.
export function minionDiscount(player) {
  return (player?.minions || []).filter((minion) => minion.ability === "discount" && minion.hp > 0).length;
}

// What Fri bar takes off each of the cards it covers.
const FREE_BAR_DISCOUNT = 2;

// The one definition of what a card costs a given player. The controller and the bot
// policy both used to carry a private copy; they import this instead so a new cost
// modifier cannot apply to two of the three and not the third.
export function defaultAdjustedCost(player, card) {
  const freeBar = player.freeBar > 0 ? FREE_BAR_DISCOUNT : 0;
  return Math.max(0, card.cost - (player.discount || 0) - freeBar - minionDiscount(player));
}

// Paying is shared by the controller and the card fuzz, so the one-shot discounts are
// spent in exactly one place: Dricks on the next card, Fri bar one card of its two.
export function payForCard(player, card, adjustedCost = defaultAdjustedCost) {
  const cost = adjustedCost(player, card);
  if (player.mana < cost) return false;
  player.mana -= cost;
  if (player.discount) player.discount = 0;
  if (player.freeBar > 0) player.freeBar -= 1;
  return true;
}

// After a card resolves: Sockerchocken counts the turn's plays, Samma igen replays the
// last one. A copy is not itself something to copy, or two of them would loop.
export function notePlayed(player, card, target) {
  player.playedCount = (player.playedCount || 0) + 1;
  if (card.id !== "samma-igen") player.lastPlayed = { cardId: card.id, targetId: target?.id ?? null };
}

// With a full couch the same person cannot be hammered twice in a row: whoever the
// previous player aimed at is off-limits to the very next player. Below five players
// the table is too small for the rule to do anything but stall the game.
export const PROTECTION_MIN_PLAYERS = 5;

export function isProtectedTarget(state, targetId) {
  if (!state || !Array.isArray(state.players) || state.players.length < PROTECTION_MIN_PLAYERS) return false;
  return Array.isArray(state.protectedIds) && state.protectedIds.includes(targetId);
}

// The filtered target list, except when filtering would empty it: a turn where no
// attack is legal would stall a bot loop and waste a human's mana, so the rule steps
// aside rather than leave nobody to hit.
export function attackableEnemies(state, enemies = []) {
  const open = enemies.filter((enemy) => !isProtectedTarget(state, enemy.id));
  return open.length ? open : enemies;
}

export function playableCardIds(player, cardById, adjustedCost = defaultAdjustedCost) {
  if (!player || !Array.isArray(player.hand)) return [];
  return player.hand.filter((cardId) => {
    const card = cardById(cardId);
    return Boolean(card && player.mana >= adjustedCost(player, card));
  });
}

export function canPlayAnyCard(player, cardById, adjustedCost = defaultAdjustedCost) {
  return playableCardIds(player, cardById, adjustedCost).length > 0;
}

// Narration is what the card actually did (minion blocks, summons). Only the last
// couple of lines are kept so the Avsluta tur instruction never scrolls out of reach.
const MAX_NARRATION_LINES = 2;

export function turnContinuationMessage(player, cardName, cardById, adjustedCost = defaultAdjustedCost, narration = []) {
  const action = canPlayAnyCard(player, cardById, adjustedCost)
    ? "Spela fler kort eller tryck Avsluta tur."
    : "Inga spelbara kort kvar. Tryck Avsluta tur.";
  const lines = Array.isArray(narration) ? narration.slice(-MAX_NARRATION_LINES) : [];
  const detail = lines.length ? `${lines.join(" ")} ` : "";
  return `${player.name} spelar ${cardName}. ${detail}${player.mana}/${player.maxMana} mana kvar. ${action}`;
}
